import { ReactNode, useEffect, useState } from 'react';
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CircleBackslashIcon,
  Cross2Icon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MoreHorizontal } from '@/components/icons/MoreHorizontal';
import { PossibleBannedDeviceTag } from '@/components/bans/PossibleBannedDeviceTag';
import { SubmitterByline } from '@/components/SubmitterByline';
import { isUnnamedSubmitter } from '@/components/bans/bannedPersonAvatar';
import { toFullS3Url } from '@/utils/toFullS3Url';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PhotoCarouselAdminView } from './adminViewPreference';
import {
  PhotoSubmissionModel,
  canApprovePhoto,
  getPhotoSubmissionStatus,
  listShowsApprove,
  listShowsReject,
} from './submissionStatus';
import { StatusChip } from './StatusChip';
import { GuestFallbackHint, SubmitterName } from './SubmitterName';
import {
  formatSubmissionDateTimeTitle,
  formatSubmissionTime,
} from './formatSubmissionTime';

export type { PhotoSubmissionModel } from './submissionStatus';

function useMinWidth(px: number) {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(`(min-width: ${px}px)`).matches
      : false,
  );
  useEffect(() => {
    const media = window.matchMedia(`(min-width: ${px}px)`);
    const apply = () => setMatches(media.matches);
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [px]);
  return matches;
}

export type PhotoSubmissionHandlers = {
  onApprove: () => void;
  onReject: () => void;
  onRequestDelete: () => void;
  onPreview: () => void;
  onBan: () => void;
  onInteract?: () => void;
};

function ActionSlot({
  width,
  children,
}: {
  width: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex justify-end" style={{ width }}>
      {children ?? <span className="h-8" />}
    </div>
  );
}

function ThumbnailButton({
  submission,
  className,
  imgClassName,
  onClick,
}: {
  submission: PhotoSubmissionModel;
  className?: string;
  imgClassName?: string;
  onClick: () => void;
}) {
  const photoSrc = submission.photoUrl ? toFullS3Url(submission.photoUrl) : '';
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'shrink-0 overflow-hidden rounded-[6px] border bg-transparent p-0',
        'cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
      aria-label="Preview photo"
    >
      <img
        className={cn(
          'object-cover',
          submission.possibleBannedDevice && 'blur-[6px]',
          imgClassName,
        )}
        src={photoSrc}
        alt={submission.caption || ''}
      />
    </button>
  );
}

const MENU_DANGER_ITEM_CLASS =
  'text-red-400 opacity-100 focus:bg-destructive/15 focus:text-red-400 data-[disabled]:opacity-100';

function MoreMenu({
  showBan,
  showDelete,
  open,
  onOpenChange,
  onBan,
  onDelete,
}: {
  showBan: boolean;
  showDelete: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onBan: () => void;
  onDelete: () => void;
}) {
  if (!showBan && !showDelete) return null;
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 !bg-transparent text-foreground"
          aria-label="More actions"
          data-name="PHOTO_SUBMISSION_MORE"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {showBan && (
          <DropdownMenuItem className={MENU_DANGER_ITEM_CLASS} onClick={onBan}>
            <CircleBackslashIcon className="mr-2 h-4 w-4" />
            Ban person…
          </DropdownMenuItem>
        )}
        {showDelete && (
          <DropdownMenuItem
            className={MENU_DANGER_ITEM_CLASS}
            onClick={onDelete}
          >
            <TrashIcon className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function PhotoSubmissionLayout({
  variant,
  submission,
  requireApproval,
  bansEnabled,
  isNew,
  moreMenuOpen,
  onMoreMenuOpenChange,
  onApprove,
  onReject,
  onRequestDelete,
  onPreview,
  onBan,
  onInteract,
}: {
  variant: PhotoCarouselAdminView;
  submission: PhotoSubmissionModel;
  requireApproval: boolean;
  bansEnabled: boolean;
  isNew?: boolean;
  moreMenuOpen?: boolean;
  onMoreMenuOpenChange?: (open: boolean) => void;
} & PhotoSubmissionHandlers) {
  const status = getPhotoSubmissionStatus(submission.approved);
  const showBan = Boolean(bansEnabled && submission.user);
  const showDelete = status === 'rejected';
  const time = formatSubmissionTime(submission.createdAt);
  const timeTitle = formatSubmissionDateTimeTitle(submission.createdAt);
  const caption = submission.caption?.trim() || '';
  const mark = () => onInteract?.();
  const isDesktopList = useMinWidth(1024);

  if (variant === 'list') {
    const showApprove = listShowsApprove(status);
    const showReject = listShowsReject(status);
    return (
      <div
        data-name="PHOTO_SUBMISSION_ROW"
        className={cn(
          'group border-b border-border last:border-b-0',
          isNew && 'bg-[rgba(255,107,61,0.07)] shadow-[inset_3px_0_0_#FF6B3D]',
          'hover:bg-[#140b06]',
        )}
        onClick={mark}
        onKeyDown={mark}
      >
        <div className="hidden h-16 items-center gap-4 px-4 lg:grid lg:grid-cols-[64px_minmax(240px,1fr)_96px_120px_328px]">
          <ThumbnailButton
            submission={submission}
            className="h-12 w-12"
            imgClassName="h-full w-full"
            onClick={() => {
              mark();
              onPreview();
            }}
          />
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <SubmitterName
                user={submission.user}
                userId={submission.userId}
                className="text-sm"
              />
              {isNew && (
                <span className="shrink-0 rounded bg-epic px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-epic-foreground">
                  NEW
                </span>
              )}
            </div>
            <div
              className={cn(
                'truncate text-xs text-muted-foreground',
                !caption && 'italic',
              )}
              title={caption || 'No caption'}
            >
              {caption || 'No caption'}
            </div>
          </div>
          <div
            className="text-[13px] tabular-nums text-muted-foreground"
            title={timeTitle}
          >
            {time}
          </div>
          <div>
            <StatusChip status={status} />
          </div>
          <div
            data-name="ACTIONS"
            className="flex items-center justify-end gap-1"
          >
            <ActionSlot width="100px">
              {showApprove ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-full"
                  onClick={onApprove}
                >
                  <CheckIcon className="h-4 w-4" />
                  Approve
                </Button>
              ) : null}
            </ActionSlot>
            <ActionSlot width="88px">
              {showReject ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-full"
                  onClick={onReject}
                >
                  <TrashIcon className="h-4 w-4" />
                  Reject
                </Button>
              ) : null}
            </ActionSlot>
            <ActionSlot width="72px">
              {showBan ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-full text-red-400 hover:bg-destructive/15 hover:text-red-400"
                  onClick={onBan}
                >
                  <CircleBackslashIcon className="h-4 w-4" />
                  Ban
                </Button>
              ) : null}
            </ActionSlot>
            <ActionSlot width="32px">
              {showDelete ? (
                <MoreMenu
                  showBan={false}
                  showDelete
                  open={isDesktopList ? moreMenuOpen : false}
                  onOpenChange={
                    isDesktopList ? onMoreMenuOpenChange : undefined
                  }
                  onBan={onBan}
                  onDelete={onRequestDelete}
                />
              ) : (
                <span className="inline-block h-8 w-8" aria-hidden="true" />
              )}
            </ActionSlot>
          </div>
        </div>

        <div className="relative flex gap-3 px-3 py-2.5 lg:hidden">
          <ThumbnailButton
            submission={submission}
            className="h-14 w-14"
            imgClassName="h-full w-full"
            onClick={() => {
              mark();
              onPreview();
            }}
          />
          <div className="min-w-0 flex-1 pr-8">
            <div className="flex min-w-0 items-center gap-2">
              <SubmitterName
                user={submission.user}
                userId={submission.userId}
                className="text-sm"
              />
              {isNew && (
                <span className="shrink-0 rounded bg-epic px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-epic-foreground">
                  NEW
                </span>
              )}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
              <span title={timeTitle}>{time}</span>
              <span aria-hidden="true">·</span>
              <StatusChip status={status} />
            </div>
            <div
              data-name="ACTIONS"
              className="mt-2 flex flex-wrap items-center gap-2"
            >
              {showApprove && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={onApprove}
                >
                  <CheckIcon className="h-4 w-4" />
                  Approve
                </Button>
              )}
              {showReject && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={onReject}
                >
                  <TrashIcon className="h-4 w-4" />
                  Reject
                </Button>
              )}
            </div>
          </div>
          <div className="absolute right-2 top-2">
            <MoreMenu
              showBan={showBan}
              showDelete={showDelete}
              open={isDesktopList ? false : moreMenuOpen}
              onOpenChange={isDesktopList ? undefined : onMoreMenuOpenChange}
              onBan={onBan}
              onDelete={onRequestDelete}
            />
          </div>
        </div>
      </div>
    );
  }

  const showApprove = canApprovePhoto(submission, requireApproval);
  const showReject = submission.approved !== false;
  return (
    <Card
      data-name="PHOTO_SUBMISSION"
      className="relative flex w-auto flex-row items-start gap-4 p-4"
    >
      <div className="relative shrink-0">
        {status !== 'approved' && (
          <Badge
            variant={status === 'rejected' ? 'destructive' : 'advisory'}
            className="absolute left-2 top-2 z-10"
          >
            {status === 'rejected' ? 'Rejected' : 'Pending'}
          </Badge>
        )}
        <ThumbnailButton
          submission={submission}
          imgClassName="w-[20vw] max-w-32"
          onClick={onPreview}
        />
      </div>
      <div className="flex min-w-0 flex-col gap-2">
        {submission.possibleBannedDevice && (
          <PossibleBannedDeviceTag
            matchedBanCreatedAt={submission.matchedBanCreatedAt}
          />
        )}
        {status === 'rejected' && submission.possibleBannedDevice && (
          <p className="text-xs font-medium text-red-400">Flagged sample</p>
        )}
        <div className="truncate text-sm" title={caption || 'No caption'}>
          {caption || (
            <span className="italic text-muted-foreground">No caption</span>
          )}
        </div>
        <SubmitterByline user={submission.user} userId={submission.userId} />
        <div className="text-xs text-muted-foreground" title={timeTitle}>
          {time}
        </div>
        <div data-name="ACTIONS" className="flex flex-row gap-2 md:flex-col">
          {showApprove && (
            <Button variant="outline" onClick={onApprove}>
              <CheckIcon className="h-4 w-4" />
              <span>Approve</span>
            </Button>
          )}
          {showReject && (
            <Button variant="outline" onClick={onReject}>
              <TrashIcon className="h-4 w-4" />
              <span>Reject</span>
            </Button>
          )}
          {showDelete && (
            <Button variant="destructive" onClick={onRequestDelete}>
              <TrashIcon className="h-4 w-4" />
              <span>Delete</span>
            </Button>
          )}
          {showBan && (
            <Button
              variant="outline"
              className="!hidden border-destructive/50 text-red-400 hover:bg-destructive/15 md:!inline-flex"
              onClick={onBan}
            >
              <CircleBackslashIcon className="h-4 w-4" />
              <span>Ban person</span>
            </Button>
          )}
          <div className="md:hidden">
            <MoreMenu
              showBan={showBan}
              showDelete={false}
              open={moreMenuOpen}
              onOpenChange={onMoreMenuOpenChange}
              onBan={onBan}
              onDelete={onRequestDelete}
            />
          </div>
        </div>
      </div>
    </Card>
  );
}

export function PhotoPreviewDialog({
  open,
  onOpenChange,
  submissions,
  currentId,
  onCurrentIdChange,
  bansEnabled,
  onApprove,
  onReject,
  onBan,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  submissions: PhotoSubmissionModel[];
  currentId: string | null;
  onCurrentIdChange: (id: string) => void;
  bansEnabled: boolean;
  onApprove: (submission: PhotoSubmissionModel) => void;
  onReject: (submission: PhotoSubmissionModel) => void;
  onBan: (submission: PhotoSubmissionModel) => void;
}) {
  const index = submissions.findIndex((item) => item.id === currentId);
  const submission = index >= 0 ? submissions[index] : submissions[0];
  const status = submission
    ? getPhotoSubmissionStatus(submission.approved)
    : 'pending';

  const go = (delta: number) => {
    if (!submissions.length) return;
    const from = index >= 0 ? index : 0;
    const next = (from + delta + submissions.length) % submissions.length;
    onCurrentIdChange(submissions[next].id);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (!submissions.length) return;
      const from = index >= 0 ? index : 0;
      if (event.key === 'ArrowRight') {
        onCurrentIdChange(submissions[(from + 1) % submissions.length].id);
      }
      if (event.key === 'ArrowLeft') {
        onCurrentIdChange(
          submissions[(from - 1 + submissions.length) % submissions.length].id,
        );
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, index, submissions, onCurrentIdChange]);

  if (!submission) return null;

  const showApprove = listShowsApprove(status);
  const showReject = listShowsReject(status);
  const showBan = Boolean(bansEnabled && submission.user);
  const photoSrc = submission.photoUrl ? toFullS3Url(submission.photoUrl) : '';
  const caption = submission.caption?.trim() || '';
  const guest = isUnnamedSubmitter(submission.user);

  const afterAction = () => {
    if (submissions.length <= 1) {
      onOpenChange(false);
      return;
    }
    const next = submissions[index + 1] ?? submissions[index - 1];
    if (next && next.id !== submission.id) onCurrentIdChange(next.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-name="PHOTO_PREVIEW_DIALOG"
        overlayClassName="bg-[rgba(0,0,0,0.85)]"
        className="h-[min(680px,90vh)] w-[min(1000px,calc(100vw-2rem))] max-w-[1000px] gap-0 overflow-hidden p-0 sm:rounded-xl [&>button.absolute]:hidden"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Photo preview</DialogTitle>
          <DialogDescription>{caption || 'Submitted photo'}</DialogDescription>
        </DialogHeader>
        <div className="grid h-full min-h-0 grid-cols-1 md:grid-cols-[1fr_320px]">
          <div className="relative flex min-h-0 items-center justify-center bg-[#050201]">
            {photoSrc ? (
              <img
                src={photoSrc}
                alt={caption || ''}
                className="h-full w-full object-contain"
              />
            ) : null}
            {submissions.length > 1 && (
              <>
                <button
                  type="button"
                  className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/80"
                  aria-label="Previous photo"
                  onClick={() => go(-1)}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/80"
                  aria-label="Next photo"
                  onClick={() => go(1)}
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs text-muted-foreground">
                  {index + 1} of {submissions.length} · ← → to browse
                </div>
              </>
            )}
          </div>
          <div className="relative flex min-h-0 flex-col gap-4 border-l border-border bg-card p-5">
            <button
              type="button"
              className="absolute right-3 top-3 text-muted-foreground"
              aria-label="Close"
              onClick={() => onOpenChange(false)}
            >
              <Cross2Icon className="h-4 w-4" />
            </button>
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Submission
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Submitted by</div>
              <div className="mt-1 flex items-center gap-2">
                <SubmitterName
                  user={submission.user}
                  userId={submission.userId}
                  className="text-sm"
                />
              </div>
              {guest && <GuestFallbackHint />}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span
                className="text-muted-foreground"
                title={formatSubmissionDateTimeTitle(submission.createdAt)}
              >
                {formatSubmissionTime(submission.createdAt)}
              </span>
              <StatusChip status={status} />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Caption</div>
              <p
                className={cn(
                  'text-sm',
                  !caption && 'italic text-muted-foreground',
                )}
              >
                {caption || 'No caption'}
              </p>
            </div>
            <div className="mt-auto flex flex-col gap-2">
              <div className="grid grid-cols-2 gap-2">
                {showApprove && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      onApprove(submission);
                      afterAction();
                    }}
                  >
                    <CheckIcon className="h-4 w-4" />
                    Approve
                  </Button>
                )}
                {showReject && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      onReject(submission);
                      afterAction();
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                    Reject
                  </Button>
                )}
              </div>
              {showBan && (
                <Button
                  variant="outline"
                  className="w-full border-destructive/50 text-red-400 hover:bg-destructive/15"
                  onClick={() => onBan(submission)}
                >
                  <CircleBackslashIcon className="h-4 w-4" />
                  Ban person
                </Button>
              )}
              <p className="text-center text-[11px] text-muted-foreground">
                After an action, the next submission opens.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeletePhotoDialog({
  open,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-name="DELETE_PHOTO_DIALOG">
        <DialogHeader>
          <DialogTitle>Delete this photo?</DialogTitle>
          <DialogDescription>
            This permanently removes the submission. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            <TrashIcon className="h-4 w-4" />
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
